// THE JOB KEPT THROUGH CHECKOUT (Studio Blueprint 04, "checkout returns to the
// job and starts it"). The pay sheet (components/pay/PaySheet) asks the studio
// on screen what the buyer was about to run (registerPayJob), keeps it in this
// tab while Stripe has them, and the studio page (StudioPage's PaidBanner)
// says when the credits are in. The studio's own flow then puts the job back
// on screen and starts it (useResumeJob, components/pay/useResumeJob).

/** The studio a pay sheet was opened in: services/studio-signin.js BACK. */
export type PayStudio = "music" | "ads" | "tiktok";

/**
 * true: the job the buyer pressed starts by itself once the credits land
 * (the blueprint). false: it only comes back on screen for one more tap.
 */
export const START_AFTER_CHECKOUT = true;

export interface PayJob {
  /** Corrido: the run whose board reopens after checkout. */
  runId?: string;
  /** What the studio needs to start the same job again (a board, a plan). */
  payload?: unknown;
}

export interface PendingJob extends PayJob {
  studio: PayStudio;
  /** Credits the job costs. */
  need: number;
  at: number;
}

// ------------------------------------------- what is on screen right now --

const current = new Map<PayStudio, () => PayJob | null>();

/** A studio flow says how to read the job it has on screen. Returns the unregister. */
export function registerPayJob(studio: PayStudio, read: () => PayJob | null): () => void {
  current.set(studio, read);
  return () => { if (current.get(studio) === read) current.delete(studio); };
}

export function currentPayJob(studio: PayStudio): PayJob | null {
  try { return current.get(studio)?.() ?? null; } catch { return null; }
}

// ------------------------------------------------ kept through checkout --

const KEY = "faceshot:pay-sheet-job";
/** A job older than this is not started by itself (a day covers OXXO cash). */
export const PENDING_TTL_MS = 24 * 60 * 60 * 1000;

export function rememberPendingJob(job: Omit<PendingJob, "at">): void {
  try { sessionStorage.setItem(KEY, JSON.stringify({ ...job, at: Date.now() })); } catch { /* no storage: the studio just opens */ }
}

export function readPendingJob(): PendingJob | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const j = JSON.parse(raw) as Partial<PendingJob>;
    if (!j || typeof j.studio !== "string" || typeof j.need !== "number" || !(j.need > 0)) return null;
    if (Date.now() - Number(j.at) > PENDING_TTL_MS) return null;
    return j as PendingJob;
  } catch {
    return null;
  }
}

export function clearPendingJob(): void {
  try { sessionStorage.removeItem(KEY); } catch { /* nothing kept */ }
}

// ----------------------------------------------- back from checkout --

// The handshake between the page that waits for the credits (PaidBanner)
// and the flow that knows how to start its own job (useResumeJob).
export type ResumeStatus = "idle" | "covered" | "late" | "cancelled";
type Listener = () => void;
const listeners = new Set<Listener>();
let status: ResumeStatus = "idle";

export function resumeStatus(): ResumeStatus {
  return status;
}

export function setResumeStatus(next: ResumeStatus): void {
  status = next;
  listeners.forEach((l) => l());
}

export function onResumeStatus(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
